import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  FileText,
  Plus,
  Printer,
  Download,
  Trash2,
  Search,
  Calendar as CalendarIcon,
  Building2,
  User,
  Package,
  X
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { API, formatCurrency } from "@/lib/utils";

// Company Details
const COMPANY_INFO = {
  name: "M/S A1 COAL BRIQUETTE",
  gstin: "10LKYPS8876H1ZY",
  address: "NEAR POKHARA MAIN ROAD, GOPALPUR, SARAN, BIHAR - 841443",
  state: "Bihar",
  stateCode: "10"
};

// Default Items
const DEFAULT_ITEMS = [
  { id: "coal", name: "Coal (कोयला)", hsn: "2701", unit: "MT", rate: 0 },
  { id: "coal_briquette", name: "Coal Briquette", hsn: "2701", unit: "MT", rate: 0 }
];

// GST Rates
const GST_RATES = [
  { value: 0, label: "0%" },
  { value: 5, label: "5%" },
  { value: 12, label: "12%" },
  { value: 18, label: "18%" },
  { value: 28, label: "28%" }
];

export const InvoiceView = () => {
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [partyNames, setPartyNames] = useState([]);
  const [items, setItems] = useState(DEFAULT_ITEMS);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  
  // New Invoice Form State
  const [invoiceDate, setInvoiceDate] = useState(new Date());
  const [customerName, setCustomerName] = useState("");
  const [customerGstin, setCustomerGstin] = useState("");
  const [customerAddress, setCustomerAddress] = useState("");
  const [customerState, setCustomerState] = useState("Bihar");
  const [customerStateCode, setCustomerStateCode] = useState("10");
  const [vehicleNumber, setVehicleNumber] = useState("");
  const [invoiceItems, setInvoiceItems] = useState([
    { name: "Coal (कोयला)", hsn: "2701", qty: 0, unit: "MT", rate: 0, gstRate: 5 }
  ]);
  const [terms, setTerms] = useState("1. Payment due within 30 days.\n2. Goods once sold will not be taken back.");

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    const token = localStorage.getItem('session_token');
    const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
    
    try {
      const [invoicesRes, customersRes, partiesRes] = await Promise.all([
        axios.get(`${API}/invoices`, { headers }),
        axios.get(`${API}/invoice-customers`, { headers }),
        axios.get(`${API}/party-names?company=A1 Coal`, { headers })
      ]);
      setInvoices(invoicesRes.data || []);
      setCustomers(customersRes.data || []);
      setPartyNames(partiesRes.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Calculate totals
  const calculateTotals = (items) => {
    let subtotal = 0;
    let totalCgst = 0;
    let totalSgst = 0;
    let totalIgst = 0;
    
    const isSameState = customerStateCode === COMPANY_INFO.stateCode;
    
    items.forEach(item => {
      const amount = (item.qty || 0) * (item.rate || 0);
      subtotal += amount;
      
      const gstAmount = amount * (item.gstRate || 0) / 100;
      
      if (isSameState) {
        totalCgst += gstAmount / 2;
        totalSgst += gstAmount / 2;
      } else {
        totalIgst += gstAmount;
      }
    });
    
    return {
      subtotal,
      cgst: totalCgst,
      sgst: totalSgst,
      igst: totalIgst,
      grandTotal: subtotal + totalCgst + totalSgst + totalIgst,
      isSameState
    };
  };

  const totals = calculateTotals(invoiceItems);

  // Add item row
  const addItemRow = () => {
    setInvoiceItems([...invoiceItems, { name: "", hsn: "", qty: 0, unit: "MT", rate: 0, gstRate: 5 }]);
  };

  // Remove item row
  const removeItemRow = (index) => {
    if (invoiceItems.length > 1) {
      setInvoiceItems(invoiceItems.filter((_, i) => i !== index));
    }
  };

  // Update item
  const updateItem = (index, field, value) => {
    const updated = [...invoiceItems];
    updated[index][field] = value;
    setInvoiceItems(updated);
  };

  // Generate invoice number
  const generateInvoiceNumber = () => {
    const date = new Date();
    const year = date.getFullYear().toString().slice(-2);
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `A1C/${year}${month}/${random}`;
  };

  // Save invoice
  const saveInvoice = async () => {
    if (!customerName) {
      toast.error("Customer name is required");
      return;
    }
    
    if (invoiceItems.every(item => !item.qty || !item.rate)) {
      toast.error("Add at least one item with quantity and rate");
      return;
    }

    const token = localStorage.getItem('session_token');
    const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
    
    const invoiceData = {
      invoice_number: generateInvoiceNumber(),
      invoice_date: format(invoiceDate, 'yyyy-MM-dd'),
      company: COMPANY_INFO,
      customer: {
        name: customerName,
        gstin: customerGstin,
        address: customerAddress,
        state: customerState,
        stateCode: customerStateCode
      },
      vehicle_number: vehicleNumber,
      items: invoiceItems.filter(item => item.qty > 0),
      totals: totals,
      terms: terms
    };

    try {
      await axios.post(`${API}/invoices`, invoiceData, { headers });
      toast.success("Invoice created successfully!");
      setShowCreateDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error("Failed to create invoice");
    }
  };

  // Reset form
  const resetForm = () => {
    setInvoiceDate(new Date());
    setCustomerName("");
    setCustomerGstin("");
    setCustomerAddress("");
    setCustomerState("Bihar");
    setCustomerStateCode("10");
    setVehicleNumber("");
    setInvoiceItems([{ name: "Coal (कोयला)", hsn: "2701", qty: 0, unit: "MT", rate: 0, gstRate: 5 }]);
  };

  // Print invoice
  const printInvoice = (invoice) => {
    const isSameState = invoice.customer.stateCode === COMPANY_INFO.stateCode;
    
    const itemRows = invoice.items.map((item, idx) => `
      <tr>
        <td class="border p-2 text-center">${idx + 1}</td>
        <td class="border p-2">${item.name}</td>
        <td class="border p-2 text-center">${item.hsn}</td>
        <td class="border p-2 text-center">${item.qty} ${item.unit}</td>
        <td class="border p-2 text-right">Rs. ${item.rate.toLocaleString('en-IN')}</td>
        <td class="border p-2 text-right">Rs. ${(item.qty * item.rate).toLocaleString('en-IN')}</td>
        <td class="border p-2 text-center">${item.gstRate}%</td>
      </tr>
    `).join('');

    const printHTML = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>Invoice</title>
<style>
* { margin: 0; padding: 0; box-sizing: border-box; }
body { font-family: Arial, sans-serif; padding: 15px; font-size: 12px; }
.header { text-align: center; border-bottom: 2px solid #000; padding-bottom: 10px; margin-bottom: 10px; }
.header h1 { font-size: 18px; margin-bottom: 5px; }
.header h2 { font-size: 14px; font-weight: normal; margin-bottom: 3px; }
.header p { font-size: 11px; color: #444; }
.invoice-title { text-align: center; font-size: 16px; font-weight: bold; margin: 15px 0; text-decoration: underline; }
.details { display: flex; justify-content: space-between; margin-bottom: 15px; }
.details-box { width: 48%; padding: 10px; border: 1px solid #000; }
.details-box h3 { font-size: 12px; margin-bottom: 5px; border-bottom: 1px solid #ccc; padding-bottom: 3px; }
.details-box p { margin: 3px 0; font-size: 11px; }
.invoice-info { display: flex; justify-content: space-between; margin-bottom: 10px; padding: 8px; background: #f5f5f5; }
.invoice-info div { font-size: 11px; }
table { width: 100%; border-collapse: collapse; margin-bottom: 15px; }
th { background: #f0f0f0; border: 1px solid #000; padding: 8px; font-size: 11px; }
td { border: 1px solid #000; padding: 6px; font-size: 11px; }
.text-right { text-align: right; }
.text-center { text-align: center; }
.totals { width: 300px; margin-left: auto; }
.totals tr td { padding: 5px 10px; border: 1px solid #ccc; }
.totals .grand-total { font-weight: bold; background: #f5f5f5; font-size: 13px; }
.amount-words { padding: 10px; background: #f9f9f9; border: 1px solid #ddd; margin-bottom: 15px; font-style: italic; }
.terms { margin-bottom: 15px; padding: 10px; border: 1px solid #ddd; }
.terms h4 { margin-bottom: 5px; font-size: 11px; }
.terms p { font-size: 10px; color: #555; white-space: pre-line; }
.signatures { display: flex; justify-content: space-between; margin-top: 40px; }
.signature-box { width: 200px; text-align: center; }
.signature-box .line { border-top: 1px solid #000; margin-top: 50px; padding-top: 5px; }
@media print { body { padding: 0; } }
</style>
</head>
<body>
<div class="header">
  <h1>${COMPANY_INFO.name}</h1>
  <h2>${COMPANY_INFO.address}</h2>
  <p><strong>GSTIN:</strong> ${COMPANY_INFO.gstin} | <strong>State:</strong> ${COMPANY_INFO.state} (${COMPANY_INFO.stateCode})</p>
</div>

<div class="invoice-title">TAX INVOICE</div>

<div class="invoice-info">
  <div><strong>Invoice No:</strong> ${invoice.invoice_number}</div>
  <div><strong>Date:</strong> ${invoice.invoice_date}</div>
</div>

<div class="details">
  <div class="details-box">
    <h3>Bill To (Customer)</h3>
    <p><strong>${invoice.customer.name}</strong></p>
    <p>${invoice.customer.address || ''}</p>
    <p><strong>GSTIN:</strong> ${invoice.customer.gstin || 'N/A'}</p>
    <p><strong>State:</strong> ${invoice.customer.state} (${invoice.customer.stateCode})</p>
  </div>
  <div class="details-box">
    <h3>Ship To</h3>
    <p><strong>${invoice.customer.name}</strong></p>
    <p>${invoice.customer.address || ''}</p>
  </div>
</div>

<table>
  <thead>
    <tr>
      <th width="5%">S.No</th>
      <th width="30%">Description</th>
      <th width="10%">HSN</th>
      <th width="15%">Qty</th>
      <th width="15%">Rate</th>
      <th width="15%">Amount</th>
      <th width="10%">GST</th>
    </tr>
  </thead>
  <tbody>
    ${itemRows}
  </tbody>
</table>

<table class="totals">
  <tr><td>Subtotal</td><td class="text-right">Rs. ${invoice.totals.subtotal.toLocaleString('en-IN')}</td></tr>
  ${isSameState ? `
    <tr><td>CGST</td><td class="text-right">Rs. ${invoice.totals.cgst.toLocaleString('en-IN')}</td></tr>
    <tr><td>SGST</td><td class="text-right">Rs. ${invoice.totals.sgst.toLocaleString('en-IN')}</td></tr>
  ` : `
    <tr><td>IGST</td><td class="text-right">Rs. ${invoice.totals.igst.toLocaleString('en-IN')}</td></tr>
  `}
  <tr class="grand-total"><td>Grand Total</td><td class="text-right">Rs. ${invoice.totals.grandTotal.toLocaleString('en-IN')}</td></tr>
</table>

<div class="amount-words">
  <strong>Amount in Words:</strong> ${numberToWords(Math.round(invoice.totals.grandTotal))} Rupees Only
</div>

<div class="terms">
  <h4>Terms & Conditions:</h4>
  <p>${invoice.terms || ''}</p>
</div>

<div class="signatures">
  <div class="signature-box">
    <div class="line">For ${COMPANY_INFO.name}<br/>Authorized Signatory</div>
  </div>
</div>

</body>
</html>`;

    // Use window.open for better Safari/MacBook compatibility
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    if (printWindow) {
      printWindow.document.write(printHTML);
      printWindow.document.close();
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.focus();
          printWindow.print();
          printWindow.onafterprint = () => printWindow.close();
        }, 300);
      };
    } else {
      // Fallback to iframe if popup blocked
      const iframe = document.createElement('iframe');
      iframe.style.position = 'absolute';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = 'none';
      iframe.style.left = '-9999px';
      document.body.appendChild(iframe);
      
      const iframeDoc = iframe.contentWindow.document;
      iframeDoc.open();
      iframeDoc.write(printHTML);
      iframeDoc.close();
      
      iframe.onload = () => {
        setTimeout(() => {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
          setTimeout(() => document.body.removeChild(iframe), 1000);
        }, 250);
      };
    }
  };

  // Number to words conversion
  const numberToWords = (num) => {
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
      'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    
    if (num === 0) return 'Zero';
    if (num < 20) return ones[num];
    if (num < 100) return tens[Math.floor(num / 10)] + (num % 10 ? ' ' + ones[num % 10] : '');
    if (num < 1000) return ones[Math.floor(num / 100)] + ' Hundred' + (num % 100 ? ' ' + numberToWords(num % 100) : '');
    if (num < 100000) return numberToWords(Math.floor(num / 1000)) + ' Thousand' + (num % 1000 ? ' ' + numberToWords(num % 1000) : '');
    if (num < 10000000) return numberToWords(Math.floor(num / 100000)) + ' Lakh' + (num % 100000 ? ' ' + numberToWords(num % 100000) : '');
    return numberToWords(Math.floor(num / 10000000)) + ' Crore' + (num % 10000000 ? ' ' + numberToWords(num % 10000000) : '');
  };

  // Delete invoice
  const deleteInvoice = async (id) => {
    if (!window.confirm("Are you sure you want to delete this invoice?")) return;
    
    const token = localStorage.getItem('session_token');
    const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
    
    try {
      await axios.delete(`${API}/invoices/${id}`, { headers });
      toast.success("Invoice deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete invoice");
    }
  };

  // Filter invoices
  const filteredInvoices = invoices.filter(inv => 
    inv.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    inv.customer?.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-24 bg-slate-200 rounded-sm animate-pulse" />
        <div className="h-64 bg-slate-200 rounded-sm animate-pulse" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="border border-slate-200 shadow-sm rounded-sm">
        <CardHeader className="border-b border-slate-100 pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="font-display font-bold text-lg tracking-tight flex items-center gap-2">
              <FileText className="w-5 h-5 text-amber-600" />
              GST Tax Invoice - {COMPANY_INFO.name}
            </CardTitle>
            <Button 
              onClick={() => setShowCreateDialog(true)}
              className="bg-amber-600 hover:bg-amber-700 text-white rounded-sm"
              data-testid="create-invoice-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Invoice
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-4">
          {/* Search */}
          <div className="flex gap-4 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by invoice number or customer..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 rounded-sm"
              />
            </div>
          </div>

          {/* Invoice List */}
          <div className="border rounded-sm overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-xs">Invoice No</TableHead>
                  <TableHead className="text-xs">Date</TableHead>
                  <TableHead className="text-xs">Customer</TableHead>
                  <TableHead className="text-xs text-right">Amount</TableHead>
                  <TableHead className="text-xs text-right">GST</TableHead>
                  <TableHead className="text-xs text-right">Total</TableHead>
                  <TableHead className="text-xs text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredInvoices.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                      No invoices found. Create your first invoice!
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredInvoices.map((invoice) => (
                    <TableRow key={invoice.id} className="hover:bg-slate-50">
                      <TableCell className="font-mono text-sm font-medium">{invoice.invoice_number}</TableCell>
                      <TableCell className="text-sm">{invoice.invoice_date}</TableCell>
                      <TableCell className="text-sm">{invoice.customer?.name}</TableCell>
                      <TableCell className="text-sm text-right font-mono">{formatCurrency(invoice.totals?.subtotal || 0)}</TableCell>
                      <TableCell className="text-sm text-right font-mono text-amber-600">
                        {formatCurrency((invoice.totals?.cgst || 0) + (invoice.totals?.sgst || 0) + (invoice.totals?.igst || 0))}
                      </TableCell>
                      <TableCell className="text-sm text-right font-mono font-semibold text-green-600">
                        {formatCurrency(invoice.totals?.grandTotal || 0)}
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => printInvoice(invoice)}
                            className="h-8 w-8 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          >
                            <Printer className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteInvoice(invoice.id)}
                            className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Create Invoice Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display font-bold flex items-center gap-2">
              <FileText className="w-5 h-5 text-amber-600" />
              Create New Tax Invoice
            </DialogTitle>
            <DialogDescription>
              {COMPANY_INFO.name} | GSTIN: {COMPANY_INFO.gstin}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Invoice Date & Vehicle */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide text-slate-500">Invoice Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal rounded-sm">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(invoiceDate, "dd MMM yyyy")}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar mode="single" selected={invoiceDate} onSelect={(d) => d && setInvoiceDate(d)} />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide text-slate-500">Vehicle Number</Label>
                <Input
                  placeholder="BR 01 AB 1234"
                  value={vehicleNumber}
                  onChange={(e) => setVehicleNumber(e.target.value.toUpperCase())}
                  className="rounded-sm"
                />
              </div>
            </div>

            {/* Customer Details */}
            <div className="p-4 bg-blue-50 rounded-sm border border-blue-200">
              <p className="text-xs font-semibold text-blue-700 flex items-center gap-2 mb-3">
                <User className="w-4 h-4" />
                CUSTOMER DETAILS
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500">Customer Name * (Select Party or Type New)</Label>
                  {/* Combined list of parties and previous customers */}
                  {(partyNames.length > 0 || customers.length > 0) ? (
                    <div className="space-y-2">
                      <Select 
                        value={customerName} 
                        onValueChange={(val) => {
                          if (val === "__custom__") {
                            setCustomerName("");
                          } else {
                            setCustomerName(val);
                          }
                        }}
                      >
                        <SelectTrigger className="rounded-sm" data-testid="invoice-party-select">
                          <SelectValue placeholder="Party/Customer चुनें" />
                        </SelectTrigger>
                        <SelectContent>
                          {partyNames.length > 0 && (
                            <>
                              <SelectItem value="__header_parties__" disabled className="text-xs text-slate-400 font-semibold">— A1 Coal Parties —</SelectItem>
                              {partyNames.map(name => (
                                <SelectItem key={`party-${name}`} value={name}>{name}</SelectItem>
                              ))}
                            </>
                          )}
                          {customers.length > 0 && (
                            <>
                              <SelectItem value="__header_customers__" disabled className="text-xs text-slate-400 font-semibold">— Previous Customers —</SelectItem>
                              {customers.filter(c => !partyNames.includes(c)).map(name => (
                                <SelectItem key={`cust-${name}`} value={name}>{name}</SelectItem>
                              ))}
                            </>
                          )}
                          <SelectItem value="__custom__">+ नया Customer जोड़ें</SelectItem>
                        </SelectContent>
                      </Select>
                      {customerName === "" && (
                        <Input
                          placeholder="नया Customer/Party Name"
                          onChange={(e) => setCustomerName(e.target.value)}
                          className="rounded-sm"
                          data-testid="invoice-custom-party-input"
                        />
                      )}
                    </div>
                  ) : (
                    <Input
                      placeholder="Customer/Party Name"
                      value={customerName}
                      onChange={(e) => setCustomerName(e.target.value)}
                      className="rounded-sm"
                    />
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500">GSTIN (Optional)</Label>
                  <Input
                    placeholder="22AAAAA0000A1Z5"
                    value={customerGstin}
                    onChange={(e) => setCustomerGstin(e.target.value.toUpperCase())}
                    className="rounded-sm"
                  />
                </div>
                <div className="col-span-2 space-y-2">
                  <Label className="text-xs text-slate-500">Address</Label>
                  <Input
                    placeholder="Customer Address"
                    value={customerAddress}
                    onChange={(e) => setCustomerAddress(e.target.value)}
                    className="rounded-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500">State</Label>
                  <Input
                    placeholder="Bihar"
                    value={customerState}
                    onChange={(e) => setCustomerState(e.target.value)}
                    className="rounded-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500">State Code</Label>
                  <Input
                    placeholder="10"
                    value={customerStateCode}
                    onChange={(e) => setCustomerStateCode(e.target.value)}
                    className="rounded-sm"
                  />
                </div>
              </div>
            </div>

            {/* Items */}
            <div className="p-4 bg-amber-50 rounded-sm border border-amber-200">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-semibold text-amber-700 flex items-center gap-2">
                  <Package className="w-4 h-4" />
                  ITEMS
                </p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addItemRow}
                  className="rounded-sm text-xs"
                >
                  <Plus className="w-3 h-3 mr-1" />
                  Add Item
                </Button>
              </div>
              
              <div className="space-y-3">
                {invoiceItems.map((item, index) => (
                  <div key={index} className="grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-3">
                      {index === 0 && <Label className="text-xs text-slate-500">Item Name</Label>}
                      <Input
                        placeholder="Item name"
                        value={item.name}
                        onChange={(e) => updateItem(index, 'name', e.target.value)}
                        className="rounded-sm text-sm"
                      />
                    </div>
                    <div className="col-span-1">
                      {index === 0 && <Label className="text-xs text-slate-500">HSN</Label>}
                      <Input
                        placeholder="HSN"
                        value={item.hsn}
                        onChange={(e) => updateItem(index, 'hsn', e.target.value)}
                        className="rounded-sm text-sm"
                      />
                    </div>
                    <div className="col-span-2">
                      {index === 0 && <Label className="text-xs text-slate-500">Qty</Label>}
                      <Input
                        type="number"
                        placeholder="0"
                        value={item.qty || ''}
                        onChange={(e) => updateItem(index, 'qty', parseFloat(e.target.value) || 0)}
                        className="rounded-sm text-sm font-mono"
                      />
                    </div>
                    <div className="col-span-2">
                      {index === 0 && <Label className="text-xs text-slate-500">Rate (₹)</Label>}
                      <Input
                        type="number"
                        placeholder="0"
                        value={item.rate || ''}
                        onChange={(e) => updateItem(index, 'rate', parseFloat(e.target.value) || 0)}
                        className="rounded-sm text-sm font-mono"
                      />
                    </div>
                    <div className="col-span-2">
                      {index === 0 && <Label className="text-xs text-slate-500">GST %</Label>}
                      <Select value={item.gstRate?.toString()} onValueChange={(v) => updateItem(index, 'gstRate', parseInt(v))}>
                        <SelectTrigger className="rounded-sm text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {GST_RATES.map(rate => (
                            <SelectItem key={rate.value} value={rate.value.toString()}>{rate.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-1">
                      {index === 0 && <Label className="text-xs text-slate-500">Amt</Label>}
                      <div className="px-2 py-2 bg-white rounded-sm border text-sm font-mono text-right">
                        {formatCurrency((item.qty || 0) * (item.rate || 0))}
                      </div>
                    </div>
                    <div className="col-span-1">
                      {invoiceItems.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeItemRow(index)}
                          className="h-9 w-9 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Totals */}
            <div className="flex justify-end">
              <div className="w-72 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Subtotal:</span>
                  <span className="font-mono">{formatCurrency(totals.subtotal)}</span>
                </div>
                {totals.isSameState ? (
                  <>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">CGST:</span>
                      <span className="font-mono">{formatCurrency(totals.cgst)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">SGST:</span>
                      <span className="font-mono">{formatCurrency(totals.sgst)}</span>
                    </div>
                  </>
                ) : (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">IGST:</span>
                    <span className="font-mono">{formatCurrency(totals.igst)}</span>
                  </div>
                )}
                <div className="flex justify-between text-base font-bold pt-2 border-t">
                  <span>Grand Total:</span>
                  <span className="font-mono text-green-600">{formatCurrency(totals.grandTotal)}</span>
                </div>
              </div>
            </div>

            {/* Terms */}
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wide text-slate-500">Terms & Conditions</Label>
              <textarea
                value={terms}
                onChange={(e) => setTerms(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm resize-none focus:outline-none focus:ring-2 focus:ring-amber-500"
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowCreateDialog(false)} className="rounded-sm">
                Cancel
              </Button>
              <Button onClick={saveInvoice} className="bg-amber-600 hover:bg-amber-700 text-white rounded-sm">
                <FileText className="w-4 h-4 mr-2" />
                Create Invoice
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
